import { useContext } from 'react';
import { Search, Calendar } from 'lucide-react';
import { License } from '../../Participants/AddParticipant/Stores/Types';
import { T, translation, TranslationsContext } from '../../../../Translations';
import SectionCard from './SectionCard';
import LicenseTypeCard from './LicenseTypeCard';

interface Props {
  /** Full list of licenses available to allocate. */
  licenses: ReadonlyArray<License>;
  /** Currently selected license IDs. */
  selectedLicenseIds: number[];
  /** Called when the selected set changes. */
  onSelectedLicenseIdsChange: (ids: number[]) => void;
  /** Search query filtering the license-type list. */
  licenseSearchQuery: string;
  onLicenseSearchQueryChange: (query: string) => void;
  /** Expanded license-type names (each rendered as an expanded card). */
  expandedLicenseTypes: Set<string>;
  onToggleLicenseTypeExpanded: (typeName: string) => void;
  /** Section number in the parent page's section list. */
  sectionNumber?: number;
}

/**
 * License selection section for the Add Group / Edit Group workflows.
 *
 * Presentational-only — all state lives in the parent. Adapted from
 * Participants/AddParticipant/LicenseSelectionSection.tsx (that version
 * couples to participantOptionsStore + formStore directly and includes
 * group-suggested-license highlighting for the Add-Participant-to-Group
 * pre-selection flow, which does not apply here). Reuses the shared License
 * type and the LicenseAvailability helper from the People tree. The per-type
 * and per-term rendering lives in LicenseTypeCard / LicenseTermRow.
 */
function GroupLicenseSelectionSection({
  licenses,
  selectedLicenseIds,
  onSelectedLicenseIdsChange,
  licenseSearchQuery,
  onLicenseSearchQueryChange,
  expandedLicenseTypes,
  onToggleLicenseTypeExpanded,
  sectionNumber = 2,
}: Props) {
  const ts = useContext(TranslationsContext);

  const licensesByType = licenses.reduce((acc, license) => {
    const typeName = license.licenseTypeName;
    if (!acc.has(typeName)) {
      acc.set(typeName, []);
    }
    acc.get(typeName)!.push(license);
    return acc;
  }, new Map<string, License[]>());

  const sortedFilteredTypes = Array.from(licensesByType.entries())
    .filter(([typeName]) => typeName.toLowerCase().includes(licenseSearchQuery.toLowerCase()))
    .map(
      ([typeName, typeLicenses]) =>
        [
          typeName,
          typeLicenses.sort((a, b) => {
            const nameA = a.licenseName.getOrElse(() => a.licenseTypeName);
            const nameB = b.licenseName.getOrElse(() => b.licenseTypeName);
            return nameA.localeCompare(nameB);
          }),
        ] as const,
    )
    .sort(([typeNameA], [typeNameB]) => typeNameA.localeCompare(typeNameB));

  return (
    <SectionCard
      number={sectionNumber}
      title={<T kind="Product Licenses" />}
      description={<T kind="Members will be allocated any license(s) you select here." />}
      intercomTarget="group-license-section"
    >
      <div className="relative mb-4">
        <input
          type="text"
          placeholder={translation('Search licenses...')(ts)}
          value={licenseSearchQuery}
          onChange={(e) => onLicenseSearchQueryChange(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-intercom-target="group-search-licenses-input"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      </div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-muted-foreground text-sm">
          <T
            kind="{{selectedCount}} of {{totalCount}} selected"
            selectedCount={selectedLicenseIds.length}
            totalCount={licenses.length}
          />
        </span>
        {selectedLicenseIds.length > 0 && (
          <button
            onClick={() => onSelectedLicenseIdsChange([])}
            className="text-primary hover:opacity-80 transition-opacity text-sm font-medium"
            data-intercom-target="group-clear-all-licenses-button"
          >
            <T kind="Clear all" />
          </button>
        )}
      </div>
      <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
        {licenses.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-border rounded-lg bg-muted/10">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <h3 className="mb-2">
              <T kind="No licenses available" />
            </h3>
            <p className="text-muted-foreground mb-4" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind="There are no active licenses. Contact your ExecOnline account team to add more licenses." />
            </p>
          </div>
        ) : sortedFilteredTypes.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-border rounded-lg bg-muted/10">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind='No licenses found matching "{{query}}"' query={licenseSearchQuery} />
            </p>
          </div>
        ) : (
          sortedFilteredTypes.map(([typeName, typeLicenses]) => (
            <LicenseTypeCard
              key={typeName}
              typeName={typeName}
              licenses={typeLicenses}
              selectedLicenseIds={selectedLicenseIds}
              onSelectedLicenseIdsChange={onSelectedLicenseIdsChange}
              expanded={expandedLicenseTypes.has(typeName)}
              onToggleExpanded={onToggleLicenseTypeExpanded}
            />
          ))
        )}
      </div>
    </SectionCard>
  );
}

export default GroupLicenseSelectionSection;
